import React, { useState } from 'react';
import {
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  Chip,
  Paper,
} from '@mui/material';
import { useQuery } from 'react-query';
import apiClient from '@uniqcamp/shared/services/api.service';
import { useSelectedSchool } from '../contexts/SelectedSchoolContext';
import { statusLabel } from '../utils/dispersalConstants';
import type { DispersalListItem, DispersalsResponse } from '../types';

const DispersalStatus: React.FC = () => {
  const { selectedSchoolId } = useSelectedSchool();
  const today = new Date().toISOString().slice(0, 10);
  const [pickupDate, setPickupDate] = useState(today);

  const dispersalsQueryKey = selectedSchoolId ? ['dispersals', pickupDate, selectedSchoolId] : ['dispersals', pickupDate];
  const { data: dispersalsData } = useQuery<DispersalsResponse>(
    dispersalsQueryKey,
    () => apiClient.get(selectedSchoolId ? `/dispersals?date=${pickupDate}&schoolId=${selectedSchoolId}` : `/dispersals?date=${pickupDate}`).then((r: { data: unknown }) => r.data as DispersalsResponse)
  );
  const allDispersals: DispersalListItem[] = dispersalsData?.dispersals ?? [];
  // Pickup status only: gate/QR requests and early dispersals that are in pickup phase (sent from class or confirmed)
  const dispersals = allDispersals.filter(
    (d) =>
      (d.requestType !== 'EARLY_DISPERSAL' && d.requestType !== 'EARLY_DISMISSAL') ||
      !!d.sentFromClass ||
      !!d.pickupConfirmed
  );

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h5" component="h1" fontWeight={600} sx={{ mb: 2 }}>
        Pickup status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        View dispersal and pickup status by date. Information only.
      </Typography>

      <TextField
        size="small"
        type="date"
        label="Date"
        value={pickupDate}
        onChange={(e) => setPickupDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: today }}
        sx={{ mb: 2 }}
      />

      <Typography variant="subtitle2" fontWeight={600} gutterBottom color="text.secondary">
        {pickupDate === today ? "Today's status" : 'Dispersal history'} for {pickupDate}
        {dispersals.length > 0 && ` · ${dispersals.length} request${dispersals.length !== 1 ? 's' : ''}`}
      </Typography>

      {dispersals.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          {pickupDate === today ? 'No dispersal requests today.' : `No dispersal requests on ${pickupDate}.`}
        </Typography>
      ) : (
        <List dense disablePadding component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
          {dispersals.map((d) => {
            const pickupDone = d.pickupConfirmed;
            const staffName = d.sentFromClassBy?.name;
            const requestCount = d.requestCount ?? 1;
            const classSection = d.studentId?.class != null ? `Class ${d.studentId.class}${d.studentId.section ? ` ${d.studentId.section}` : ''}` : null;
            const requestType = d.requestSource === 'MANUAL_REQUEST' ? 'Request' : d.requestSource === 'QR_SCAN' ? 'QR scan' : null;
            return (
              <ListItem
                key={d._id}
                sx={{
                  flexWrap: 'wrap',
                  gap: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:last-child': { borderBottom: 0 },
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {d.studentId?.name}
                  </Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, py: 0.5, '& li': { mb: 0.25 } }}>
                    {classSection != null && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Class &amp; section:</Box> {classSection}</Typography></li>
                    )}
                    {d.gateName && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Gate name:</Box> {d.gateName}</Typography></li>
                    )}
                    {requestType && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Request type:</Box> {requestType}</Typography></li>
                    )}
                    {(d.requestedAt || d.createdAt) && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Request time:</Box> {new Date(d.requestedAt || d.createdAt!).toLocaleString()}</Typography></li>
                    )}
                    {requestCount > 1 && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Requested {requestCount} times</Box></Typography></li>
                    )}
                    {staffName && d.status === 'COMPLETED' && d.sentFromClass && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Dispersed By:</Box> {staffName}</Typography></li>
                    )}
                    {d.pickupConfirmed && d.pickupConfirmedAt && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Pickup confirmed time:</Box> {new Date(d.pickupConfirmedAt).toLocaleString()}</Typography></li>
                    )}
                    {d.pickupConfirmed && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Pickup confirmation By:</Box> {d.autoConfirmed ? 'auto' : `parent${d.pickupConfirmedBy?.name ? ` (${d.pickupConfirmedBy.name})` : ''}`}</Typography></li>
                    )}
                    {(d.requestType === 'EARLY_DISPERSAL' || d.requestType === 'EARLY_DISMISSAL') && (
                      <li><Typography variant="body2" color="text.secondary"><Box component="span" fontWeight={600}>Early dispersal</Box></Typography></li>
                    )}
                  </Box>
                </Box>
                {pickupDone && (
                  <Chip
                    label={
                      d.autoConfirmed
                        ? `Pickup confirmed (auto)`
                        : `Pickup confirmed${d.pickupConfirmedBy?.name ? ` by ${d.pickupConfirmedBy.name}` : ''}`
                    }
                    size="small"
                    color="success"
                    sx={{ mb: 0.5 }}
                  />
                )}
                {!pickupDone && (
                  <Chip
                    label={statusLabel[d.status] ?? d.status}
                    size="small"
                    color={d.status === 'COMPLETED' ? 'success' : 'default'}
                    sx={{ mb: 0.5 }}
                  />
                )}
              </ListItem>
            );
          })}
        </List>
      )}
    </Box>
  );
};

export default DispersalStatus;
